import { useNavigate, useParams, Link as RRDLink } from "react-router-dom";
import { APPLICATION_STATUS_ACCEPTING, LABELS, LAYOUT_ADJUST_SPACE } from "./constants";
import { COMMON_LABELS, getDivisionLabel } from "../default-constants";
import {
  ColumnLayout,
  SpaceBetween,
  Input,
  Container,
  TextContent,
  Box,
  Button,
  Textarea,
  Grid,
} from "@cloudscape-design/components-themed";
import { Tab } from "./InfoTab";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { useGetApplicationContentQuery } from "../../features/api";
import { useCodelistHook } from "../../app/useCodelistHook";
import { formatDate, formatDateTime } from "../../utils/format";
import { CSDict } from "../../features/api-interfaces";
import { useEffect, useState } from "react";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { ICompany } from "../application-incompletion-reason-register/Main";
import { DiffCheckModal } from "./DiffCheckModal";
import { getDiffCompanyInfo } from "./util";
import { isEmpty } from "lodash";

/**
 * 申請内容照会
 */
export const ApplicationInquiryMain = () => {
  const navigator = useNavigate();

  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const isProgress = useHasAuthorityHook(PERMISSIONS.PROGRESS);
  const isRateRead = useHasAuthorityHook(PERMISSIONS.RATE_READ);
  const isApply = useHasAuthorityHook(PERMISSIONS.APPLY);
  const isDocomoStaffUser = isDocomoUser && !!isProgress && !!isRateRead;
  const isDocomoMasterUser =
    !!useHasAuthorityHook(PERMISSIONS.MASTER_EDIT) && isDocomoUser;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const returnToApplicationStatus = () => {
    navigator("/application-status?isBackFromDetail=true");
  };

  const { no } = useParams();
  const { data, refetch, isFetching } = useGetApplicationContentQuery(
    no as string
  );
  const { diffOfficialCompany, diffApplicationCompany } = getDiffCompanyInfo(
    data?.companyInfo.official,
    data?.companyInfo.application,
    isDocomoStaffUser
  );
  const hasDiffCompany =
    !isEmpty(diffOfficialCompany) || !isEmpty(diffApplicationCompany);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const {
    applicationStatus,
    applicationDetailStatus,
    applicationType,
    companyType,
    applicationFormType
  } = useCodelistHook();

  const sharedContent = data?.sharedContent;
  const applicationData = data?.companyInfo.application;
  const officialData = data?.companyInfo.official;
  const stores = data ? data.storeInfo : [];

  const isAcceptingApplication =
    sharedContent?.applicationStatusCodes.length === 1 &&
    sharedContent.applicationStatusCodes[0] === APPLICATION_STATUS_ACCEPTING;

  const enumerateCodeLabel = (value: string[], codeList: CSDict[]) => {
    //　配列かどうかチェック
    if (!Array.isArray(value) || value.length === 0) {
      return "ー";
    }

    const labels = value.map((value) => {
      const item = codeList.find((item) => item.value === value);
      return item ? item.label : "ー";
    });
    return labels.join("/");
  };

  const enumerateCodeLabelToLabel = (value: string[], codeList: CSDict[]) => {
    //　配列かどうかチェック
    if (!Array.isArray(value) || value.length === 0) {
      return "ー";
    }

    const labels = value.map((value) => {
      const item = codeList.find((item) => item.label === value);
      return item ? item.label : "ー";
    });
    return labels.join("/");
  };

  const enumerateCodeLabelMasterUser = (
    value: string[],
    codeList: CSDict[]
  ) => {
    //　配列かどうかチェック
    if (!Array.isArray(value) || value.length === 0) {
      return "ー";
    }

    const labels = value
      .map((value) => {
        const item = codeList.find(
          (item) => item.value === value && value === "01"
        );
        return item ? item.label : "";
      })
      .filter((item) => item !== "");
    if (labels === undefined || labels.length === 0) {
      return "ー";
    } else {
      return labels.join("/");
    }
  };

  /**
   * 申請情報確認・追加登録ボタン押下時の処理
   */

  const individualHandlingConf = () => {
    navigator(`/individual-handling-confirmation-result/${no}`);
  };

  /**
   * 申請差戻ボタンを押したときの処理.
   */
  const applicationSendBack = () => {
    const company: ICompany = {
      corporateNumber: applicationData?.corporateNumber,
      companyName: applicationData?.companyName,
      address: applicationData?.address,
      merchantName: applicationData?.merchantName,
    };
    navigator(`/application-status/${no}/incompletion-reason-register`, {
      state: company,
    });
  };

  const rateCheckButtonOnClick = () => {
    const companyData = {
      corporateNo: applicationData?.corporateNumber,
      companyName: applicationData?.companyName,
      address: applicationData?.address,
      merchantName: applicationData?.merchantName,
      commissionRate: applicationData?.settlementFeeRate,
    };
    navigator(`/application-status/${no}/update`, { state: companyData });
  };

  return (
    <>
    <SpaceBetween direction="vertical" size="l">
        <Grid gridDefinition={[{colspan: 9}]}>
          <Container>
          <Grid gridDefinition={isApply ? [{colspan:4},{colspan:4},{colspan:4}] : [{colspan:6},{colspan:6}]}>
              <SpaceBetween size="m" direction="horizontal">
              <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                <TextContent>
                  <h5>{LABELS.APPLICATION_NO + LAYOUT_ADJUST_SPACE.SPACE_6_SIZE}</h5>
                </TextContent>
                <Input value={no ?? ""} readOnly />
              </Grid>
              <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                <TextContent>
                  <h5>{LABELS.APPLICATION_STATUS + LAYOUT_ADJUST_SPACE.SPACE_6_SIZE}</h5>
                </TextContent>
                <Input
                  value={
                    sharedContent
                      ? enumerateCodeLabel(
                          sharedContent.applicationStatusCodes,
                          applicationStatus
                        )
                      : ""
                  }
                  readOnly
                />
              </Grid>
              <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
              {isDocomoUser && (
                  <TextContent>
                    <h5>{LABELS.APPLICATION_DETAIL_STATUS + LAYOUT_ADJUST_SPACE.SPACE_4_SIZE}</h5>
                  </TextContent>
                )}

                {isDocomoMasterUser ? (
                  <Input
                    value={
                      sharedContent
                        ? enumerateCodeLabel(
                            sharedContent.applicationDetailStatusCodes,
                            applicationDetailStatus
                          )
                        : ""
                    }
                    readOnly
                  />
                ) : (
                  isDocomoUser && (
                    <Input
                      value={
                        sharedContent
                          ? enumerateCodeLabelMasterUser(
                              sharedContent.applicationDetailStatusCodes,
                              applicationDetailStatus
                            )
                          : ""
                      }
                      readOnly
                    />
                  )
                )}
              </Grid>
              <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                <TextContent>
                    <h5>{LABELS.APPLICATION_TYPE + LAYOUT_ADJUST_SPACE.SPACE_6_SIZE}</h5>
                  </TextContent>
                  <Input
                    value={
                      sharedContent
                        ? getDivisionLabel(
                            applicationType,
                            sharedContent.applicationTypeCode
                          )
                        : ""
                    }
                    readOnly
                  />
              </Grid>
              <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
              {
                  isDocomoUser && <>
                    <TextContent>
                      <h5>{LABELS.APPLICANT_USER_TYPE + LAYOUT_ADJUST_SPACE.SPACE_5_SIZE}</h5>
                    </TextContent>
                    <Input
                      value={
                        sharedContent
                          ? getDivisionLabel(
                              companyType,
                              sharedContent.applicantUserType
                            )
                          : ""
                      }
                      readOnly
                    />
                  </>
                }
              </Grid>
              </SpaceBetween>
              <SpaceBetween size="m" direction="horizontal">
                <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                  <TextContent>
                    <h5>{LABELS.FILING_DATE + LAYOUT_ADJUST_SPACE.SPACE_7_SIZE}</h5>
                  </TextContent>
                  <Input
                    value={sharedContent ? formatDate(sharedContent.createdAt) : ""}
                    readOnly
                  />
                </Grid>
                <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                  <TextContent>
                    <h5>{LABELS.ANSWER_START_TIME + LAYOUT_ADJUST_SPACE.SPACE_6_SIZE}</h5>
                  </TextContent>
                  <Input
                    value={
                      sharedContent?.fixedTime
                        ? formatDateTime(sharedContent.fixedTime)
                        : COMMON_LABELS.HYPHEN
                    }
                    readOnly
                  />
                </Grid>
                <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                  {isDocomoUser && (
                    <TextContent>
                      <h5>{LABELS.INDIVIDUAL_HANDLING_STATUTE_OF_LIMITATIONS + LAYOUT_ADJUST_SPACE.SPACE_4_SIZE}</h5>
                    </TextContent>
                  )}
                  {isDocomoUser &&
                    (sharedContent?.individualAttentionFlag ? (
                      <RRDLink
                        to={`/individual-handling-confirmation-result/${no}`}
                      >
                        {LABELS.YES}
                      </RRDLink>
                    ) : (
                      <Input
                        value={sharedContent ? LABELS.NONE : COMMON_LABELS.BLANK}
                        readOnly
                      />
                    ))}
                </Grid>
                <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                <TextContent>
                    <h5>{LABELS.NUMBER_OF_STORES_TENANTS + LAYOUT_ADJUST_SPACE.SPACE_7_SIZE}</h5>
                  </TextContent>
                  <Input
                    value={
                      sharedContent ? sharedContent.shopQuantity.toString() : ""
                    }
                    readOnly
                  />
                </Grid>
                <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                {isDocomoUser && (
                  <>
                    <TextContent>
                      <h5>{LABELS.APPLICANT_EMAIL}</h5>
                    </TextContent>
                    <Input
                      value={sharedContent ? sharedContent.createdBy : ""}

                      readOnly
                    />
                  </>
                )}
                </Grid>
                </SpaceBetween>
                <SpaceBetween size="m" direction="horizontal">
                  <Grid gridDefinition={[{colspan:6},{colspan:6}]}>
                  {
                    isApply && (
                      <>
                        <TextContent>
                          <h5>{LABELS.APPLICATION_FORM_TYPE + LAYOUT_ADJUST_SPACE.SPACE_5_SIZE}</h5>
                        </TextContent>
                        <Input
                          value={sharedContent && sharedContent.applicationFormType ? 
                            enumerateCodeLabelToLabel(sharedContent.applicationFormType, applicationFormType) : COMMON_LABELS.HYPHEN}
                          readOnly
                        />
                      </>
                    )
                  }
                  </Grid>
                </SpaceBetween>
            </Grid>
          </Container>
        </Grid>
        <Container>
          <Tab
            appStatusCodes={applicationStatus}
            appDetailStatusCodes={applicationDetailStatus}
            stores={stores}
            officialCompany={officialData}
            applicationCompany={applicationData}
            isDocomoUser={isDocomoUser}
            isDocomoStaffUser={isDocomoStaffUser}
            isDocomoMasterUser={isDocomoMasterUser}
            isFetching={isFetching}
            hasDiffCompany={hasDiffCompany}
            setIsModalOpen={setIsModalOpen}
          />
        </Container>

        <Container>
          <Box>
            <Box margin="s" float="left">
              <TextContent>
                <h5>{LABELS.REJECTION_REASON}</h5>
              </TextContent>
            </Box>
            <ColumnLayout columns={1}>
              <Textarea
                value={sharedContent ? sharedContent.incompletionReason : ""}
                rows={2}
                readOnly
              />
            </ColumnLayout>
          </Box>
        </Container>

        <ColumnLayout columns={2}>
          <Box float="left">
            <Button
              formAction="none"
              variant="link"
              onClick={returnToApplicationStatus}
            >
              {COMMON_LABELS.BTNS.BACK}
            </Button>
          </Box>

          {isDocomoUser && (
            <Box float="right" margin="l">
              <SpaceBetween direction="horizontal" size="xxl">
                <Button
                  variant="normal"
                  disabled={!sharedContent?.individualAttentionFlag}
                  onClick={individualHandlingConf}
                >
                  {LABELS.CONFIRMATION_OF_INDIVIDUAL_ATTENTION}
                  <br />
                  {LABELS.ADDITIONAL_REGISTRATION}
                </Button>
                <Button
                  variant={"primary"}
                  onClick={applicationSendBack}
                  disabled={!isAcceptingApplication}
                >
                  {LABELS.APPLY_FOR_A_JOB}
                </Button>
                <Button
                  variant="primary"
                  disabled={!sharedContent?.rateCheckingFlag}
                  onClick={rateCheckButtonOnClick}
                >
                  {LABELS.CONFIRMATION_OF_APPLICATION_INFORMATION}
                </Button>
              </SpaceBetween>
            </Box>
          )}
        </ColumnLayout>
      </SpaceBetween>
      <DiffCheckModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        diffOfficialCompany={diffOfficialCompany}
        diffApplicationCompany={diffApplicationCompany}
      />
    </>
  );
};
